import styles from '@/styles/about.module.css';

import { useEffect } from 'react';
import {
  IconArrowDown,
  IconArrowRight,
  IconArrowUpRight,
  IconBook2,
  IconBuilding,
  IconCertificate,
  IconFingerprint,
  IconMessageCircle,
  IconShieldCheck,
} from '@tabler/icons-react';
import Image from 'next/image';
import Link from 'next/link';
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import { useFetchCmsContentByType } from '@/components/modules/Landing/api';
import { CMS_PAGE_TITLES } from '@/types/entities/cms.entities';

const focusAreas = [
  {
    number: '01',
    icon: IconMessageCircle,
    title: 'A better front door for your customers.',
    body: 'Agents that answer questions from your content, help visitors find what they need, and connect them with your team when a conversation needs a person.',
    label: 'Customer-facing agents',
    href: '/docs/website-integration',
  },
  {
    number: '02',
    icon: IconBuilding,
    title: 'Institutional knowledge, within reach.',
    body: 'Private agents that make policies, procedures, and shared documents useful in everyday work, with access controlled for the people you choose.',
    label: 'Internal knowledge',
    href: '/docs/private-app-chatbot-mcp',
  },
  {
    number: '03',
    icon: IconBook2,
    title: 'Deeper research. Traceable sources.',
    body: 'Specialist agents built around a defined body of knowledge, helping professionals explore complex records and return to the source to verify an answer.',
    label: 'Expert knowledge agents',
    href: '/use-cases/energy',
  },
];

const experience = [
  {
    label: 'Public service',
    title: 'Cybercrime & digital evidence',
    body: 'Former Director of the Cybercrimes Investigations Unit at the Puerto Rico Department of Justice. Established its digital forensics laboratory and authored Puerto Rico’s first digital evidence guide for prosecutors.',
  },
  {
    label: 'Enterprise experience',
    title: 'Cybersecurity & risk',
    body: 'Former senior cyber strategy consultant at Deloitte, advising a U.S. Government agency. His experience also includes Fortune 500 cybersecurity advisory and oversight of critical energy infrastructure.',
  },
  {
    label: 'Education & teaching',
    title: 'Making complex technology understandable',
    body: 'Has trained thousands of judges, prosecutors, and law enforcement officers, including international teaching engagements for the U.S. Department of State and Department of Justice’s OPDAT program.',
  },
  {
    label: 'Product building',
    title: 'From ideas to working technology',
    body: 'Founder of SA Cyber and Chat.co, bringing legal, technical, and business experience to enterprise AI. Built Blendapps, one of the first applications on Google App Engine, in partnership with AOL; it drew more than 7 million visits from 216 countries. Named inventor on U.S. Patent No. 8,214,272, covering website valuation.',
  },
];

const safeguards = [
  {
    icon: IconBook2,
    title: 'Evidence you can inspect',
    body: 'Ground answers in your approved knowledge and use source citations to check the underlying material. AI supports professional judgment; it does not replace it.',
  },
  {
    icon: IconFingerprint,
    title: 'Control over your knowledge',
    body: 'Choose your sources and audience. Authenticated private agents and role-based access help you manage how organizational knowledge is shared.',
  },
  {
    icon: IconShieldCheck,
    title: 'Clear data practices',
    body: 'Encryption at rest and in transit, with published policies explaining how information is handled. Ask us about the controls and configuration for your use case.',
  },
];

const AboutUsPage = () => {
  const { data: siteSettings } = useFetchCmsContentByType(CMS_PAGE_TITLES.SITESETTING);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
  }, []);

  return (
    <div className={styles.page}>
      <NextSeo
        title="About Chat.co — Built by SA Cyber"
        description="Meet the people and purpose behind Chat.co. Created by SA Cyber and founded by Rafael Sosa Arvelo, we bring cybersecurity, governance, and AI engineering to organizational knowledge."
        canonical="https://chat.co/about-us"
        openGraph={{
          url: 'https://chat.co/about-us',
          title: 'About Chat.co — Built by SA Cyber',
          description:
            'Cybersecurity roots. Practical AI. Meet the company and founder behind Chat.co.',
          images: [
            {
              url: 'https://chat.co/images/og-about.png',
              width: 1200,
              height: 630,
              alt: 'About Chat.co',
            },
          ],
        }}
      />
      <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: 'Home', item: 'https://chat.co' },
          { position: 2, name: 'About us', item: 'https://chat.co/about-us' },
        ]}
      />
      <a href="#about-content" className={styles.skipLink}>
        Skip to content
      </a>
      <Header contentData={siteSettings?.cmsPage?.pageData ?? undefined} />
      <main id="about-content" tabIndex={-1}>
        <section className={styles.hero} aria-labelledby="about-title">
          <div className={`${styles.container} ${styles.heroGrid}`}>
            <div>
              <p className={styles.eyebrow}>
                <span /> ABOUT CHAT.CO
              </p>
              <h1 id="about-title">
                Built for the work
                <br />
                where <em>trust matters.</em>
              </h1>
              <p className={styles.heroIntro}>
                Your organization’s knowledge deserves the same care as the decisions it informs.
              </p>
              <p className={styles.heroBody}>
                Chat.co was created by SA Cyber to help people put AI to work with their own
                knowledge, clear sources, and control over who can access it.
              </p>
              <a href="#our-story" className={styles.textLink}>
                Get to know us <IconArrowDown size={17} aria-hidden="true" />
              </a>
            </div>
            <div
              className={styles.heritage}
              aria-label="Chat.co was created by SA Cyber, combining cybersecurity, governance, and AI engineering">
              <div className={styles.foundation}>
                <p className={styles.smallLabel}>OUR FOUNDATION</p>
                <Image
                  src="/images/about/sa-cyber-logo.png"
                  alt="SA Cyber"
                  width={1005}
                  height={226}
                  priority
                  sizes="(max-width: 760px) 75vw, 320px"
                  className={styles.parentLogo}
                />
                <p className={styles.foundationCaption}>
                  Cybersecurity. Governance. AI engineering.
                </p>
              </div>
              <div className={styles.productSignature}>
                <div className={styles.connection}>
                  <span>THE COMPANY BEHIND</span>
                  <IconArrowDown size={16} stroke={1.5} aria-hidden="true" />
                </div>
                <Image
                  src="/images/about/chatco-logo-white.png"
                  alt="Chat.co"
                  width={905}
                  height={218}
                  priority
                  sizes="(max-width: 760px) 70vw, 280px"
                  className={styles.productLogo}
                />
                <p className={styles.productCaption}>Knowledge, put to work.</p>
                <a href="#our-certification" className={styles.heroCertification}>
                  <IconShieldCheck size={24} stroke={1.5} aria-hidden="true" />
                  <span>
                    <strong>ISO/IEC 27001:2022 certified</strong>
                    <span>Information security management</span>
                  </span>
                  <IconArrowDown size={15} aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>
        </section>
        <nav className={styles.sectionNav} aria-label="On this page">
          <div className={styles.container}>
            <a href="#our-story">
              <span>01</span> Our story
            </a>
            <a href="#our-focus">
              <span>02</span> Our focus
            </a>
            <a href="#our-work">
              <span>03</span> In practice
            </a>
            <a href="#our-founder">
              <span>04</span> Our founder
            </a>
            <a href="#our-commitments">
              <span>05</span> Our commitments
            </a>
          </div>
        </nav>
        <section id="our-story" className={styles.section} aria-labelledby="story-title">
          <div className={`${styles.container} ${styles.storyGrid}`}>
            <div>
              <p className={styles.eyebrow}>OUR STORY</p>
              <h2 id="story-title">
                Cybersecurity roots.
                <br />
                <em>A practical purpose.</em>
              </h2>
            </div>
            <div className={styles.prose}>
              <p className={styles.lead}>
                Behind Chat.co is SA Cyber: a team bringing together cybersecurity practitioners, AI
                engineers, and product builders.
              </p>
              <p>
                SA Cyber’s background spans government, enterprise security, and regulated
                industries. Its work combines cybersecurity advisory, custom AI systems, and
                software development—disciplines that meet in Chat.co.
              </p>
              <p>
                The problem we focus on is simple: organizations have valuable knowledge, but
                getting it to the right person at the right moment is hard. Documents accumulate.
                Expertise gets siloed. Teams and customers need a clearer way to find answers.
              </p>
              <p>
                Chat.co makes that knowledge accessible through AI agents that organizations can
                create and manage without writing code. Our purpose is to make AI useful in everyday
                work while keeping evidence, access, and accountability in view.
              </p>
              <a
                href="https://www.sa-cyber.com/about"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.textLink}>
                Meet SA Cyber <IconArrowUpRight size={17} aria-hidden="true" />
                <span className="sr-only"> (opens in a new tab)</span>
              </a>
            </div>
          </div>
        </section>
        <section
          id="our-focus"
          className={`${styles.section} ${styles.focus}`}
          aria-labelledby="focus-title">
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>WHAT WE FOCUS ON</p>
                <h2 id="focus-title">
                  Useful AI.
                  <br />
                  <em>For real responsibilities.</em>
                </h2>
              </div>
              <p>
                One platform. Three ways to make your organization’s knowledge more useful to the
                people who depend on it.
              </p>
            </div>
            <div className={styles.focusGrid}>
              {focusAreas.map((area) => (
                <article key={area.number} className={styles.focusCard}>
                  <div className={styles.cardTop}>
                    <area.icon size={28} stroke={1.4} aria-hidden="true" />
                    <span>{area.number}</span>
                  </div>
                  <h3>{area.title}</h3>
                  <p>{area.body}</p>
                  <Link href={area.href} className={styles.textLink}>
                    {area.label}
                    <IconArrowRight size={17} aria-hidden="true" />
                  </Link>
                </article>
              ))}
            </div>
            <div className={styles.platformLinks}>
              <p className={styles.smallLabel}>BEYOND THE CONVERSATION</p>
              <Link href="/docs/voice-lines" className={styles.textLink}>
                Voice agents <IconArrowUpRight size={15} aria-hidden="true" />
              </Link>
              <Link href="/docs/channel-integrations" className={styles.textLink}>
                Messaging integrations <IconArrowUpRight size={15} aria-hidden="true" />
              </Link>
              <Link href="/docs/document-studio" className={styles.textLink}>
                Research & document creation <IconArrowUpRight size={15} aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>
        <section id="our-work" className={styles.section} aria-labelledby="work-title">
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>CHAT.CO IN PRACTICE</p>
                <h2 id="work-title">
                  The work behind <em>the words.</em>
                </h2>
              </div>
              <p>
                From complex regulatory records to everyday school policies, our platform serves
                people with very different questions.
              </p>
            </div>
            <div className={styles.workGrid}>
              <article className={styles.workCard}>
                <div className={styles.workImage}>
                  <Image
                    src="/images/landing/maxeta-energy.png"
                    alt="Electricity transmission infrastructure"
                    fill
                    sizes="(max-width: 760px) 100vw, 50vw"
                    className={styles.energyImage}
                  />
                </div>
                <div className={styles.workBody}>
                  <p className={styles.smallLabel}>ENERGY & REGULATORY RESEARCH</p>
                  <h3>MAXeta Energy</h3>
                  <p>
                    Dedicated expert agents help analysts navigate filings, orders, and testimony,
                    with citations back to the regulatory record.
                  </p>
                  <Link
                    href="https://chat.co/case-studies/maxeta-energy"
                    className={styles.textLink}>
                    Explore the MAXeta story <IconArrowRight size={17} aria-hidden="true" />
                  </Link>
                </div>
              </article>
              <article className={styles.workCard}>
                <div className={styles.workImage}>
                  <Image
                    src="/images/landing/calvario-academy.png"
                    alt="Teacher and students in a classroom"
                    fill
                    sizes="(max-width: 760px) 100vw, 50vw"
                    className={styles.schoolImage}
                  />
                </div>
                <div className={styles.workBody}>
                  <p className={styles.smallLabel}>EDUCATION & INSTITUTIONAL KNOWLEDGE</p>
                  <h3>Calvario City Christian Academy</h3>
                  <p>
                    A faculty knowledge portal puts manuals, procedures, and policies within reach,
                    helping staff find the guidance they need.
                  </p>
                  <Link
                    href="https://chat.co/case-studies/calvario-academy"
                    className={styles.textLink}>
                    Explore the Calvario story <IconArrowRight size={17} aria-hidden="true" />
                  </Link>
                </div>
              </article>
            </div>
            <div className={styles.advisoryNote}>
              <span className={styles.smallLabel}>PROFESSIONAL SERVICES</span>
              <p>
                At Sosa & Arvelo, Chat.co also supports research across client documents for
                strategy, risk, and financial advisory work.
              </p>
            </div>
          </div>
        </section>
        <section
          id="our-founder"
          className={`${styles.section} ${styles.founder}`}
          aria-labelledby="founder-title">
          <div className={styles.container}>
            <div className={styles.founderGrid}>
              <figure className={styles.founderPortrait}>
                <Image
                  src="/images/about/rafael-sosa-arvelo.jpg"
                  alt="Rafael Sosa Arvelo speaking at a lectern"
                  width={639}
                  height={854}
                  sizes="(max-width: 760px) 100vw, 380px"
                />
                <figcaption>
                  <span>RAFAEL SOSA ARVELO</span>Founder, SA Cyber & Chat.co
                </figcaption>
              </figure>
              <div>
                <p className={styles.eyebrow}>MEET OUR FOUNDER</p>
                <h2 id="founder-title">
                  Rafael Sosa Arvelo<span className={styles.esq}>, Esq.</span>
                </h2>
                <p className={styles.founderLead}>
                  At the intersection of law,
                  <br />
                  cybersecurity, and artificial intelligence.
                </p>
                <p className={styles.founderIntro}>
                  Rafael founded SA Cyber and Chat.co with a perspective shaped by public service,
                  enterprise advisory, and technology entrepreneurship. That experience informs a
                  practical question: how can organizations use powerful technology responsibly?
                </p>
                <div className={styles.experienceGrid}>
                  {experience.map((item) => (
                    <div key={item.label}>
                      <p className={styles.smallLabel}>{item.label}</p>
                      <h3>{item.title}</h3>
                      <p>{item.body}</p>
                    </div>
                  ))}
                </div>
                <a
                  href="https://patents.google.com/patent/US8214272B2/en"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.textLink}>
                  View the patent record <IconArrowUpRight size={17} aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </div>
            </div>
            <div className={styles.credentials}>
              <div>
                <p className={styles.smallLabel}>EDUCATION</p>
                <p>
                  Harvard University · Master’s in Finance (ALM)
                  <br />
                  University of Puerto Rico School of Law · J.D.
                  <br />
                  University of Massachusetts Amherst · B.B.A.
                </p>
              </div>
              <div>
                <p className={styles.smallLabel}>BAR ADMISSIONS</p>
                <p>
                  District of Columbia
                  <br />
                  Commonwealth of Puerto Rico
                </p>
              </div>
              <div>
                <p className={styles.smallLabel}>A SHARED PERSPECTIVE</p>
                <p>
                  Legal judgment. Security discipline.
                  <br />
                  Technology that serves people.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section
          id="our-commitments"
          className={`${styles.section} ${styles.commitments}`}
          aria-labelledby="commitments-title">
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <div>
                <p className={styles.eyebrow}>HOW WE EARN YOUR TRUST</p>
                <h2 id="commitments-title">
                  Accountability,
                  <br />
                  <em>built into the work.</em>
                </h2>
              </div>
              <p>
                Trust grows when people can understand a system, question an answer, and make
                informed decisions about their data.
              </p>
            </div>
            <div id="our-certification" className={styles.certification}>
              <div className={styles.certificationIcon}>
                <IconCertificate size={30} stroke={1.4} aria-hidden="true" />
              </div>
              <div className={styles.certificationDetails}>
                <p className={styles.smallLabel}>INDEPENDENTLY CERTIFIED</p>
                <h3>ISO/IEC 27001:2022 certified</h3>
                <p>
                  SA CYBER LLC’s information security management system covering the design,
                  development, hosting, operation, and support of Chat.co is certified by GCAI.
                </p>
                <dl className={styles.certificateFacts}>
                  <div>
                    <dt>Certificate</dt>
                    <dd>GCAI-ISMS-LM73</dd>
                  </div>
                  <div>
                    <dt>Certified since</dt>
                    <dd>
                      <time dateTime="2026-08-29">August 29, 2026</time>
                    </dd>
                  </div>
                  <div>
                    <dt>Valid through</dt>
                    <dd>
                      <time dateTime="2029-08-28">August 28, 2029</time>
                    </dd>
                  </div>
                </dl>
                <a
                  href="/certifications/sa-cyber-iso-27001-2022.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.textLink}>
                  View certificate (PDF) <IconArrowUpRight size={17} aria-hidden="true" />
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
              </div>
            </div>
            <div className={styles.safeguards}>
              {safeguards.map((item) => (
                <div key={item.title}>
                  <item.icon size={28} stroke={1.4} aria-hidden="true" />
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              ))}
            </div>
            <div className={styles.trustDetails}>
              <p>
                <span className={styles.statusDot} />
                SOC 2 Type II audit in progress
              </p>
              <div>
                <Link href="/privacy" className={styles.textLink}>
                  Privacy & security <IconArrowRight size={17} aria-hidden="true" />
                </Link>
                <Link href="/privacy-policy" className={styles.textLink}>
                  Read our privacy policy <IconArrowRight size={17} aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </section>
        <section className={styles.contact} aria-labelledby="contact-title">
          <div className={`${styles.container} ${styles.contactGrid}`}>
            <div>
              <p className={styles.eyebrow}>LET’S TALK</p>
              <h2 id="contact-title">
                Put your knowledge
                <br />
                <em>in good company.</em>
              </h2>
              <p>
                Tell us what your team needs to know, who needs access, and what a useful answer
                looks like. We’ll start there.
              </p>
            </div>
            <div className={styles.contactActions}>
              <Link href="/contact-us" className={styles.primaryButton}>
                Talk with our team <IconArrowUpRight size={19} aria-hidden="true" />
              </Link>
              <Link href="/demo" className={styles.textLink}>
                Explore Chat.co <IconArrowRight size={17} aria-hidden="true" />
              </Link>
              <span>Built by SA Cyber. Built for your work.</span>
            </div>
          </div>
        </section>
      </main>
      <Footer contentData={siteSettings?.cmsPage?.pageData ?? undefined} />
    </div>
  );
};
export default AboutUsPage;
